import React from 'react';
import DetailedItem from './DetailedItem';
import styled from 'styled-components/macro';
import { CurrencyCodeRecord } from 'currency-codes';
import { TFunction } from 'i18next';
import { validatePositiveNumber, validateRequired } from '../../../helpers/validation';
import { useTranslation } from 'react-i18next';
import { fromMonetaryAmountString, fromQuantityString } from '../../../helpers/formatters/money';
import { Money } from '../../../components/Formatters';
import { DetailedItemValue } from '../types';
import { colors, distances } from '../../../styles/constants';
import { itemsVatAmountExp } from './vat';


export const defaultItem = (defaultVat?: string) => {
    const id = Math.random().toString(36).substring(7);
    return {
        id,
        line_id: id,
        description: '',
        amount: '',
        quantity: '1',
        vat: defaultVat || '0',
    };
};

interface DetailedItemsProps {
    defaultVat?: string;
    items: DetailedItemValue[];
    currency: CurrencyCodeRecord;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFormikState: any;
    errors: any;
    touched: any;
    shouldAutoFocusOnNewItem: boolean;
}

export const detailedItemsValidation = (items: DetailedItemValue[], t: TFunction) => {
    return items
        .map((item, idx) => {
            return [
                {
                    path: `items[${idx}].amount`,
                    validator: validateRequired(
                        t('payments.virtual_terminal.validation.invalid', {
                            fieldName: t('payments.virtual_terminal.fields.items.amount'),
                        })
                    ),
                },
                {
                    path: `items[${idx}].amount`,
                    validator: validatePositiveNumber(
                        t('payments.virtual_terminal.validation.invalid', {
                            fieldName: t('payments.virtual_terminal.fields.items.amount'),
                        })
                    ),
                },
                {
                    path: `items[${idx}].quantity`,
                    validator: validateRequired(
                        t('payments.virtual_terminal.validation.invalid', {
                            fieldName: t('payments.virtual_terminal.fields.items.quantity'),
                        })
                    ),
                },
                {
                    path: `items[${idx}].quantity`,
                    validator: validatePositiveNumber(
                        t('payments.virtual_terminal.validation.invalid', {
                            fieldName: t('payments.virtual_terminal.fields.items.quantity'),
                        })
                    ),
                },
                {
                    path: `items[${idx}].description`,
                    validator: validateRequired(
                        t('payments.virtual_terminal.validation.invalid', {
                            fieldName: t('payments.virtual_terminal.fields.items.description'),
                        })
                    ),
                },
            ];
        })
        .reduce((acc, el) => [...acc, ...el], []);
};

export const itemsAmount = (items: DetailedItemValue[], lng: string) => {
    return items.reduce((acc, item) => {
        return acc + fromQuantityString(lng, item.amount) * parseInt(item.quantity);
    }, 0);
};

const itemsAmountExp = (items: DetailedItemValue[], lng: string, currency: string) => {
    return items.reduce((acc, item) => {
        return acc + fromMonetaryAmountString(lng, item.amount, currency) * parseInt(item.quantity);
    }, 0);
};

const DetailedItems = (
    {
        items,
        currency,
        onChange,
        onBlur,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        shouldAutoFocusOnNewItem,
        defaultVat,
    }: DetailedItemsProps
) => {
    const { t, i18n } = useTranslation();
    const lng = i18n.language;

    return (
        <>
            {items.map((item, idx) => {
                const namePrefix = `items[${idx}]`;
                const itemErrors = (errors?.items ?? [])[idx];
                const itemTouched = (touched?.items ?? [])[idx];
                return (
                    <DetailedItem
                        key={namePrefix}
                        namePrefix={namePrefix}
                        item={item}
                        onChange={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            if (target.name.endsWith('amount') || target.name.endsWith('quantity')) {
                                if (item.amount !== '') {
                                    setFieldTouched(`${namePrefix}.amount`);
                                }
                            }
                            if (target.name.endsWith('vat')) {
                                const value = target.value;
                                if (value) {
                                    setFieldValue('defaultVat', value);
                                }
                            }
                            onChange(e);
                        }}
                        onRemove={(id: string) => {
                            const newItems = items.filter((item) => item.line_id !== id);
                            if (newItems.length === 0) {
                                newItems.push(defaultItem(defaultVat));
                            }
                            setFieldValue('items', newItems, true);
                        }}
                        currency={currency}
                        onBlur={onBlur}
                        touched={itemTouched}
                        errors={itemErrors}
                        autoFocus={item.description === '' && item.amount === '' && shouldAutoFocusOnNewItem}
                    />
                );
            })}
            <Amounts className="wide">
                <TextWrapper className="w-25">{t('payments.virtual_terminal.fields.items_total')}</TextWrapper>
                <TextWrapper className="w-15" />
                <TextWrapper className="w-15">
                </TextWrapper>
                <TextWrapper className="w-15" />
                <TextWrapper className="w-15">
                    <Money amount={itemsVatAmountExp(items, lng, currency.code) || 0} currency={currency.code} />
                </TextWrapper>
                <TextWrapper className="w-15-edge">
                    <Money amount={itemsAmountExp(items, lng, currency.code) || 0} currency={currency.code} />
                </TextWrapper>
                {errors.amount && touched.amount && (
                    <div>
                        <Message className="invalid">{errors.amount}</Message>
                    </div>
                )}
            </Amounts>
            <Amounts className="small">
                <TextWrapper>{t('payments.virtual_terminal.fields.items_total')}</TextWrapper>
                <TextWrapper>
                    <Money amount={itemsAmountExp(items, lng, currency.code) || 0} currency={currency.code} />
                </TextWrapper>
                {errors.amount && touched.amount && (
                    <div>
                        <Message className="invalid">{errors.amount}</Message>
                    </div>
                )}
            </Amounts>
        </>
    );
};

const Amounts = styled.div`
    display: flex;

    @media (max-width: 1100px) {
        justify-content: space-between;

        &.wide {
            display: none;
        }
    }

    @media (min-width: 1101px) {
        &.small {
            display: none;
        }
    }
`;

const Message = styled.label`
    font-size: 11px;
    line-height: ${distances.small};

    &.invalid {
        color: ${colors.invalid};
    }
`;

const TextWrapper = styled.div`
    &.w-25 {
        width: 25%;
    }
    &.w-15 {
        width: 15%;
    }
    &.w-15-edge {
        width: 15%;
        margin-right: 0;
    }
    margin-right: 5px;
`;

export default DetailedItems;
